
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface UseLLMAgentProps {
  apiKeyId: string | null;
}

export const useLLMAgent = ({ apiKeyId }: UseLLMAgentProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateResponse = async (systemPrompt: string, userInput: string) => {
    if (!apiKeyId) {
      setError('No API key selected');
      toast({
        title: 'Error',
        description: 'Please select an API key to use',
        variant: 'destructive',
      });
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase.functions.invoke('llm-agent', {
        body: {
          apiKeyId,
          systemPrompt,
          userInput,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data.response;
    } catch (err: any) {
      console.error('Error generating LLM response:', err);
      setError(err.message || 'Failed to generate response');
      toast({
        title: 'Error',
        description: `Failed to generate response: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateResponse,
    isLoading,
    error,
  };
};

export default useLLMAgent;
